import {
	ReactElement,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { LiveTileContent } from "../LiveTileContent";
import { contains } from "client/Data/searching";
import { AlertManager, createLocalDataSource, useAlerts } from "client/Hooks";
import { HowToListItem } from "./HowToListItem";
import {
	HowToVideoDialogModel,
	HowToVideoModel,
	HowToVideoStatus,
	setStatus
} from "client/Api/Workspace/LiveInformation/HowTos/howToLiveInformation";
import { HowToInterestedParty, HowToListModel, HowToLiveInformationProvider } from "./provider";
import { BusyIndicator } from "client/Components";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { HowToDialog } from "./Dialog/HowToDialog";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { TileContext } from "../TileContext";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

type LoadingState = {
	isLoading: boolean;
	isStatusChanging: boolean;
	hasTooManyConsecutiveErrors?: boolean;
	hasGlobalFailure?: boolean;
	response?: HowToListModel;
};

type HowToTabs = {
	uniqueId: Guid;
	name: string;
	videoStatus: HowToVideoStatus;
	count?: number;
};

const tabs: readonly HowToTabs[] = [
	{
		uniqueId: newGuid(),
		name: localize("_UnwatchedHowTos"),
		videoStatus: HowToVideoStatus.Unwatched,
	},
	{
		uniqueId: newGuid(),
		name: localize("_WatchedHowTos"),
		videoStatus: HowToVideoStatus.Watched,
	}
];;

export type HowToLiveTileContentProps = {
	provider: HowToLiveInformationProvider;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

const useHowTosDataSource = createLocalDataSource<HowToVideoModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) =>
				contains(item.title, searchTerm) ||
				contains(item.description, searchTerm),
		}
	})
	.getHook();

export const HowToLiveTileContent = (props: HowToLiveTileContentProps): ReactElement => {
	const {
		provider,
		onOpenTileMenu
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isStatusChanging: false,
		isLoading: true,
	});
	const [dialogVideo, setDialogVideo] = useState<HowToVideoDialogModel | null>(null);
	const [unsavedStatusChanges, setUnsavedStatusChanges] = useState<Map<string, HowToVideoStatus>>(new Map);
	const activeTab: HowToTabs | undefined = tabs.find(x => x.uniqueId === activeTabId);
	const alertManager: AlertManager = useAlerts();

	const {
		isLoading,
		isStatusChanging,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		response
	} = loadingState;
	const videosToRender: HowToVideoModel[] = response?.status === "success"
		? response.videos
		: [];

	const { currentItems } = useHowTosDataSource(
		videosToRender, {
		searchTerm,
	});

	const {
		id: tileId,
		icon,
		name,
		environmentAppId,
		dimension: dimensionToRender,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const isCompactView: boolean = dimensionToRender.dimension.columnSpan < 6 || dimensionToRender.kind === "responsive";

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension: dimensionToRender,
		appIsLicensed,
		appIsNotAccessible
	});

	const tabsToRender = useMemo(
		(): readonly HowToTabs[] => {
			return tabs.map(x => {
				const count: number | undefined = response?.status === "success"
					? x.videoStatus === HowToVideoStatus.Unwatched
						? response.unwatchedVideosCount
						: response.watchedVideosCount
					: undefined;

				return {
					...x,
					count
				};
			});
		},
		[response]
	);

	const party: HowToInterestedParty = useMemo(
		() => {
			return {
				updateList: (listModel: HowToListModel) => {
					setLoadingState({
						isLoading: false,
						isStatusChanging: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						response: listModel
					});
					if(listModel?.status === "success") {
						setUnsavedStatusChanges(prev => {
							const copy: Map<string, HowToVideoStatus> = new Map(prev);
							listModel.videos.forEach(x => {
								copy.delete(x.subject);
							});
							return copy;
						});
					}
				},
				partyId: tileId,
				resourceId: "howtos",
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						isStatusChanging: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: true,
						response: undefined
					});
					setUnsavedStatusChanges(new Map);
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						isStatusChanging: false,
						hasTooManyConsecutiveErrors: true
					}));
				},
			};
		},
		[tileId]
	);

	useEffect(
		() => {
			provider.register(party, {});

			return () => provider.unregister(party);
		},
		[party, provider]
	);

	useEffect(
		() => {
			const status: HowToVideoStatus | undefined = activeTab?.videoStatus;
			provider.changeInterests(party, {
				watched: status === HowToVideoStatus.Watched,
				unwatched: status === HowToVideoStatus.Unwatched
			});
		},
		[activeTab?.videoStatus, party, provider]
	);

	const changeHandler = async (videoSubject: string, status: HowToVideoStatus): Promise<void> => {
		if (isStatusChanging) {
			return;
		}
		setLoadingState(x => ({
				...x,
				isStatusChanging: true
		}));
		setUnsavedStatusChanges(prev => {
			const copy: Map<string, HowToVideoStatus> = new Map(prev);
			copy.set(videoSubject, status);
			return copy;
		});
		try {
			await setStatus({
				status,
				videoSubject
			});
			provider.forceReload();
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
		}
	};

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const renderContent = (): ReactElement | null => {
		if (isLoading) {
			return null;
		}

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasGlobalFailure || response?.status === "error") {
			return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
		}

		if (videosToRender.length === 0) {
			return <LiveTileContent.Message text={localize("_NoHowTosToShow")} />;
		}

		if (currentItems.length === 0) {
			return <LiveTileContent.Message text={localize("_NoResultsFound")} />;
		}

		return (
			<LiveTileContent.List>
				{currentItems.map(x => (
					<HowToListItem
						key={x.subject}
						subject={x.subject}
						title={x.title}
						description={x.description}
						youtubeId={x.youtubeId}
						status={unsavedStatusChanges.get(x.subject) ?? x.status}
						isCompactView={isCompactView}
						onStatusChange={(newStatus) => changeHandler(x.subject, newStatus)}
						onOpenDialog={() => setDialogVideo(x)}
						onClick={addAnalyticsEvent}
					/>
				))}
			</LiveTileContent.List>
		);
	};

	return (
		<>
			<LiveTileContent
				name={name}
				icon={icon}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
			>
				<LiveTileContent.Header icon={icon}>
					<LiveTileContent.TabSelector
						value={activeTabId}
						onChange={setActiveTabId}
						tabs={tabsToRender}
						tileId={tileId}
						tileName={name}
					/>
				</LiveTileContent.Header>
				<LiveTileContent.Content>
					<LiveTileContent.ContentStack>
						<LiveTileContent.SearchInput
							onChange={setSearchTerm}
							placeholder={localize("_Search")}
						/>

						<LiveTileContent.BusyIndicatorContainer>
							{isLoading && (
								<BusyIndicator />
							)}
							<LiveTileContent.Scrollable>
								{renderContent()}
							</LiveTileContent.Scrollable>
						</LiveTileContent.BusyIndicatorContainer>
					</LiveTileContent.ContentStack>
				</LiveTileContent.Content>
			</LiveTileContent>
			{dialogVideo && (
				<HowToDialog
					allVideos={videosToRender}
					video={dialogVideo}
					onClose={() => setDialogVideo(null)}
				/>
			)}
		</>
	);
};
HowToLiveTileContent.displayName = "HowToLiveTileContent";
